import { ListingsSection } from './section-listings'

type PillEntry = {
  $input: HTMLInputElement | null
  $searchPill: HTMLButtonElement
  enabled: boolean
}

export class ListingsFilter {
  private parent: ListingsSection
  private $element: Element
  private $searchPillsWrapper: HTMLDivElement
  private $regionPillTemplate: HTMLSpanElement
  private $toggleButton: HTMLButtonElement
  private $drawer: HTMLSpanElement

  private open: boolean = false

  private pillMap: Map<string, PillEntry> = new Map()

  constructor(parent: ListingsSection, $element: Element) {
    this.parent = parent
    this.$element = $element

    this.$searchPillsWrapper =
      this.$element.querySelector('.listings-filter__search-pills') as HTMLDivElement

    const $regionSelect =
      this.$element.querySelector('.listings-filter__region-select') as HTMLSelectElement

    $regionSelect.addEventListener('change', this.handleRegionSelectChange.bind(this))

    this.$regionPillTemplate =
      this.$element.querySelector('.listings-filter__region-pill-template .pill') as HTMLSpanElement

    // Handle filter accordion
    this.$toggleButton =
      this.$element.querySelector('.listings-filter__btn-toggle') as HTMLButtonElement
    this.$drawer =
      this.$element.querySelector('.listings-filter__drawer') as HTMLDivElement

    this.$toggleButton.addEventListener('click', (event) => {
      event.preventDefault()
      this.toggle()
    })

    const $filterPills = $element.querySelectorAll('.listings-filter__pill')
    for (const $filterPill of $filterPills) {
      const $input = $filterPill.querySelector('input') as HTMLInputElement
      const $pill = $filterPill.querySelector('.pill') as HTMLSpanElement
      if ($input !== null && $pill !== null) {
        this.registerPill(
          $input.name as 'type' | 'category' | 'region',
          $input.value,
          $pill,
          $input
        )
        $input.addEventListener(
          'change',
          this.handlePillCheckboxChange.bind(this)
        )
      }
    }
  }

  toggle(open?: boolean): void {
    if (open !== this.open) {
      this.open = open ?? !this.open
      this.$toggleButton.setAttribute('aria-expanded', this.open ? 'true' : 'false')
      this.$drawer.setAttribute('aria-hidden', this.open ? 'false' : 'true')
      this.$element.classList.toggle('listings-filter--open', this.open)
    }
  }

  handlePillCheckboxChange(event: Event): void {
    const $input = event.target as HTMLInputElement
    this.toggleFilter(
      $input.name as 'type' | 'category',
      $input.value,
      $input.checked
    )
  }

  handleRegionSelectChange(event: Event): void {
    const $select = event.target as HTMLSelectElement
    this.toggleFilter(
      'region',
      $select.value
    )
    $select.value = ''
  }

  registerPill(
    name: 'type' | 'category' | 'region',
    value: string,
    $pill: Element,
    $input: HTMLInputElement | null
  ): void {
    const $searchPill = document.createElement('button')
    $searchPill.classList.add('listings-filter__search-pill')
    $searchPill.appendChild($pill.cloneNode(true))
    $searchPill.addEventListener('click', (event) => {
      event.preventDefault()
      this.toggleFilter(name, value, false)
    })

    this.pillMap.set(`${name}:${value}`, {
      $input,
      // For each pill, we prepare a clone that can be placed in the
      // search field
      $searchPill,
      enabled: $input?.checked ?? false,
    })
  }

  toggleFilter(
    name: 'type' | 'category' | 'region',
    value: string,
    checked?: boolean
  ): void {
    const filters = this.parent.getFilters()
    let filterValues = filters[name] ?? []

    const previousChecked = filterValues.includes(value)
    const newChecked = checked ?? !previousChecked

    if (checked !== previousChecked) {
      if (newChecked) {
        // Add filter value
        this.parent.setFilters({
          [name]: [...(filters[name] ?? []), value]
        })
      } else {
        // Remove filter value
        this.parent.setFilters({
          [name]: filterValues.filter(string => string !== value)
        })
      }
    }
  }

  updateFilterPills(): void {
    const filters = this.parent.getFilters()

    // Lazily create pills for region filters
    for (const region of filters.region ?? []) {
      if (this.pillMap.has(`region:${region}`)) {
        continue
      }

      const $pill = this.$regionPillTemplate.cloneNode(true) as HTMLSpanElement
      const $label = $pill.querySelector('.pill__label') as HTMLSpanElement
      if ($label !== null) {
        $label.textContent = region
      }
      this.registerPill('region', region, $pill, null)
    }

    for (const [key, entry] of this.pillMap.entries()) {
      const [name, value] = key.split(':')

      const checked =
        filters[name as 'type' | 'category']?.includes(value) ?? false

      if (checked !== entry.enabled) {
        entry.enabled = checked

        // Add or remove the pill to or from the search field
        if (checked) {
          this.$searchPillsWrapper.appendChild(entry.$searchPill)
        } else {
          this.$searchPillsWrapper.removeChild(entry.$searchPill)
        }

        // Check or uncheck the filter pill
        if (entry.$input !== null) {
          entry.$input.checked = checked
        }
      }
    }
  }
}
